import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tag, Phone, MessageCircle, X } from "lucide-react";

interface FloatingContactBarProps {
  onQuoteClick: () => void;
}

const FloatingContactBar = ({ onQuoteClick }: FloatingContactBarProps) => {
  const [isVisible, setIsVisible] = useState(true);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 md:bottom-6 inset-x-0 z-[100] flex justify-center items-center px-4 pointer-events-none"
        >
          {/* Reduced desktop max-width from 480px to 420px for a sleeker profile */}
          <div className="relative w-full max-w-[340px] md:max-w-[420px] pointer-events-auto">
            
            {/* Main Container with Glassmorphism */}
            <div className="relative p-[1px] overflow-hidden rounded-full shadow-[0_15px_40px_rgba(0,0,0,0.4)] border border-white/20">
              
              {/* Animated Gradient Border */}
              <div 
                className="absolute inset-[-200%] animate-[spin_6s_linear_infinite] opacity-30"
                style={{
                  background: "conic-gradient(from 0deg, transparent, #a855f7, #3b82f6, transparent)",
                }}
              />

              {/* 
                  THE GLASS CORE:
                  Changed desktop height from h-14 to h-12 to make it more compact.
              */}
              <div className="relative flex items-center h-11 md:h-12 bg-black/40 backdrop-blur-md rounded-full px-2 md:px-3 border-t border-white/10">
                
                {/* 1. QUOTE */}
                <button
                  onClick={onQuoteClick}
                  className="flex-1 flex items-center justify-center gap-1.5 hover:bg-white/10 rounded-full h-4/5 transition-all group"
                >
                  <Tag className="w-3.5 md:w-3.5 h-3.5 md:h-3.5 text-purple-400 shrink-0" />
                  <span className="text-[10px] md:text-[10.5px] font-bold uppercase tracking-wider text-white">Get Quote</span>
                </button>

                <div className="w-[1px] h-4 md:h-5 bg-white/20 mx-0.5 md:mx-1" />

                {/* 2. CALL */}
                <a 
                  href="tel:+971588243660"
                  className="flex-1 flex items-center justify-center gap-1.5 hover:bg-white/10 rounded-full h-4/5 transition-all"
                >
                  <Phone className="w-3.5 md:w-3.5 h-3.5 md:h-3.5 text-blue-400 shrink-0" />
                  <span className="text-[10px] md:text-[10.5px] font-bold uppercase tracking-wider text-white">Call</span>
                </a>

                <div className="w-[1px] h-4 md:h-5 bg-white/20 mx-0.5 md:mx-1" />

                {/* 3. WHATSAPP */}
                <a 
                  href="https://wa.me/971588243660"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 hover:bg-white/10 rounded-full h-4/5 transition-all"
                >
                  <MessageCircle className="w-3.5 md:w-3.5 h-3.5 md:h-3.5 text-green-400 shrink-0" />
                  <span className="text-[10px] md:text-[10.5px] font-bold uppercase tracking-wider text-white">WhatsApp</span>
                </a>

                {/* Close Button */}
                <button 
                  onClick={() => setIsVisible(false)}
                  className="flex items-center justify-center p-1.5 ml-0.5 hover:bg-white/10 rounded-full text-white/50 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingContactBar;